/**
 * GPT Loader - Singleton for loading Google Publisher Tag
 * Ensures GPT is loaded only once and services are enabled correctly
 * Includes timeout protection and retry capability
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { withRetry, withTimeout } from '../utils';
// Configuration
const GPT_LOAD_TIMEOUT_MS = 15000; // 15 seconds to load GPT
const GPT_RETRY_ATTEMPTS = 2; // Retry once on failure
export class GPTLoader {
    /**
     * Load GPT script and enable services with retry and timeout
     */
    static load(privacyConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            // Store privacy config on first call
            if (privacyConfig && !this.privacyConfig) {
                this.privacyConfig = privacyConfig;
            }
            // Already loaded
            if (this.loaded) {
                return;
            }
            // Currently loading - return existing promise
            if (this.loading && this.loadPromise) {
                return this.loadPromise;
            }
            // If previous load failed, allow retry
            if (this.loadFailed) {
                this.loadFailed = false;
                this.loadPromise = null;
            }
            // Start loading with retry
            this.loading = true;
            this.loadPromise = withRetry(() => this.loadInternal(), {
                maxAttempts: GPT_RETRY_ATTEMPTS,
                initialDelayMs: 2000,
                onRetry: (attempt, error) => {
                    console.warn(`[GetJarAds] GPT load attempt ${attempt} failed, retrying...`, error.message);
                },
            }).catch((error) => {
                this.loading = false;
                this.loadFailed = true;
                throw error;
            });
            return this.loadPromise;
        });
    }
    /**
     * Internal load logic with timeout
     */
    static loadInternal() {
        return __awaiter(this, void 0, void 0, function* () {
            return withTimeout(() => this.loadScript(), GPT_LOAD_TIMEOUT_MS, `GPT script load timed out after ${GPT_LOAD_TIMEOUT_MS}ms`);
        });
    }
    /**
     * Core script loading logic
     */
    static loadScript() {
        // SSR guard: GPT requires a browser environment. Reject cleanly under Node/SSR
        // so callers can branch on a typed error rather than a ReferenceError.
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return Promise.reject(new Error('[GetJarAds] GPT cannot load: no browser environment (SSR)'));
        }
        return new Promise((resolve, reject) => {
            var _a;
            try {
                // Initialize googletag command queue
                window.googletag = window.googletag || { cmd: [] };
                // Check if GPT is already loaded (by another script)
                if (typeof ((_a = window.googletag) === null || _a === void 0 ? void 0 : _a.apiReady) !== 'undefined') {
                    this.loaded = true;
                    this.loading = false;
                    resolve();
                    return;
                }
                // Load GPT script
                const script = document.createElement('script');
                script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
                script.async = true;
                script.onerror = () => {
                    this.loading = false;
                    reject(new Error('Failed to load Google Publisher Tag'));
                };
                document.head.appendChild(script);
                // Configure and enable GPT services
                window.googletag.cmd.push(() => {
                    try {
                        // Apply privacy settings BEFORE enableServices()
                        if (this.privacyConfig) {
                            if (this.privacyConfig.gdpr || this.privacyConfig.coppa) {
                                // Request non-personalized ads for GDPR/COPPA compliance
                                window.googletag.pubads().setPrivacySettings({
                                    restrictDataProcessing: true,
                                });
                                console.log('[GetJarAds] Privacy settings applied: restricted data processing enabled');
                            }
                            // Apply consent string if provided
                            if (this.privacyConfig.consentString) {
                                window.googletag.pubads().setPrivacySettings({
                                    restrictDataProcessing: true,
                                });
                                console.log('[GetJarAds] Privacy settings applied: restricted data processing for consent string');
                            }
                        }
                        // Configure GPT using the modern setConfig API (replaces the
                        // deprecated `pubads().enableSingleRequest()` / `collapseEmptyDivs()`).
                        //
                        // - `singleRequest: true` — batch slot requests.
                        // - `collapseDivs: 'DISABLED'` — DO NOT auto-collapse empty slots.
                        //   The Prebid Universal Creative (PUC, served by AY's prebid
                        //   line items) is a 1×1 third-party creative that paints
                        //   asynchronously: it XHRs the cached `adm` before it has any
                        //   markup, then resizes the slot via SafeFrame postMessage.
                        //   With `'BEFORE_FETCH'` (or the legacy `collapseEmptyDivs(true)`)
                        //   GPT collapses the container to 1×1 before the PUC's resize
                        //   arrives — the slot never expands and the user sees a blank
                        //   ad even though the cached creative loaded successfully.
                        //   No-fill collapse is now event-driven instead: the banner
                        //   renderer hides the container on `slotRenderEnded` with
                        //   `event.isEmpty === true` (see gpt-banner.ts).
                        // Note: we intentionally do NOT disable initial load — out-of-page
                        // slots (interstitials) require it to auto-fetch on `display()`.
                        window.googletag.setConfig({
                            singleRequest: true,
                            collapseDivs: 'DISABLED',
                        });
                        // Enable services
                        window.googletag.enableServices();
                        this.loaded = true;
                        this.loading = false;
                        console.log('[GetJarAds] Google Publisher Tag loaded successfully');
                        resolve();
                    }
                    catch (error) {
                        this.loading = false;
                        reject(error);
                    }
                });
            }
            catch (error) {
                this.loading = false;
                reject(error);
            }
        });
    }
    /**
     * Check if GPT is loaded
     */
    static isLoaded() {
        return this.loaded;
    }
    /**
     * Reset loader state (for testing)
     */
    static reset() {
        this.loaded = false;
        this.loading = false;
        this.loadPromise = null;
    }
}
GPTLoader.loaded = false;
GPTLoader.loading = false;
GPTLoader.loadPromise = null;
GPTLoader.privacyConfig = null;
GPTLoader.loadFailed = false;
